// ============================================================================
// Ciclo de desarrollo (#559) — tipos y reglas PURAS de la línea de tiempo.
// ============================================================================
// Separado de serviceDeskService.ts a propósito: ese módulo importa `utils/api`
// → firebase, y en un worktree sin apps/v5/.env cualquier prueba que lo cargue
// revienta al colectar (`auth/invalid-api-key`). Aquí sólo hay datos y
// funciones puras, así que se prueban sin montar nada.
//
// Los IDS de etapa son fijos —la API máquina, las transiciones automáticas y
// la vista del cliente dependen de ellos—; lo que cada mesa configura es el
// nombre, el color y si usa o no cada etapa (settings.stages). Las siete
// primeras son el carril lineal; las dos últimas son laterales: interrumpen el
// carril sin perder el sitio.
// ============================================================================

export type DevStageId =
  | "received"
  | "analysis"
  | "development"
  | "pr_review"
  | "qa_deploy"
  | "staff_validated"
  | "resolved"
  | "waiting_customer"
  | "qa_rejected";

export interface StageOption {
  id: DevStageId;
  label: string;
  color: string;
  kind: "linear" | "side";
  terminal?: boolean;
  enabled?: boolean;
}

/** Un peldaño de la línea de tiempo tal como lo pinta la UI. */
export interface StageStep {
  id: DevStageId;
  label: string;
  color: string;
  kind?: "linear" | "side";
  /** Epoch ms en que se alcanzó, o null si todavía no. */
  at: number | null;
  status: "done" | "current" | "pending";
}

/** Enlaces técnicos: SOLO llegan en la proyección interna. */
export interface DevStageLinks {
  issueUrl?: string | null;
  prUrl?: string | null;
  qaUrl?: string | null;
  branch?: string | null;
  repo?: string | null;
}

export type DevStageSource = "system" | "agent" | "staff";

export interface DevStageHistoryEntry {
  stage: DevStageId;
  label: string;
  at: number | null;
  source: DevStageSource;
  actorName?: string | null;
  note?: string | null;
}

/**
 * La etapa tal como llega del backend. La vista del cliente trae SOLO
 * `current`/`label`/`enteredAt`/`timeline`: sin `links`, sin `history`, sin
 * quién la movió.
 */
export interface DevStage {
  current: DevStageId;
  label: string;
  enteredAt: number | null;
  /** Fecha en que LLEGÓ el ticket (no se reescribe en cada transición). */
  receivedAt?: number | null;
  timeline: StageStep[];
  resumeStage?: DevStageId | null;
  links?: DevStageLinks;
  history?: DevStageHistoryEntry[];
}

/**
 * Espejo de DEV_STAGES del backend (serviceDeskConstants.js). Sólo es el
 * respaldo cuando la mesa no configuró etapas: el nombre y el color REALES
 * vienen siempre del backend, que ya aplicó la configuración de la mesa.
 */
export const DEV_STAGE_META: Record<DevStageId, { label: string; color: string; kind: "linear" | "side" }> = {
  received: { label: "Recibido", color: "#64748B", kind: "linear" },
  analysis: { label: "En análisis", color: "#3B82F6", kind: "linear" },
  development: { label: "En desarrollo", color: "#8B5CF6", kind: "linear" },
  pr_review: { label: "PR en revisión", color: "#0EA5E9", kind: "linear" },
  qa_deploy: { label: "Desplegado en QA", color: "#14B8A6", kind: "linear" },
  staff_validated: { label: "Validado por staff", color: "#22C55E", kind: "linear" },
  resolved: { label: "Resuelto", color: "#10B981", kind: "linear" },
  waiting_customer: { label: "Esperando cliente", color: "#F59E0B", kind: "side" },
  qa_rejected: { label: "Rechazado en QA", color: "#EF4444", kind: "side" },
};

export const DEV_STAGE_ORDER: DevStageId[] = [
  "received",
  "analysis",
  "development",
  "pr_review",
  "qa_deploy",
  "staff_validated",
  "resolved",
  "waiting_customer",
  "qa_rejected",
];

/** Etapas que el staff puede elegir en «Mover a»: las que la mesa tiene activas. */
export function selectableStages(settings: { stages?: StageOption[] } | null | undefined): StageOption[] {
  const list: StageOption[] = settings?.stages?.length
    ? settings.stages
    : DEV_STAGE_ORDER.map((id) => ({ id, ...DEV_STAGE_META[id], enabled: true }));
  return list.filter((s) => s.enabled !== false);
}

/** ¿Hay algún enlace técnico que enseñar en la vista interna? */
export function hasStageLinks(links: DevStageLinks | undefined | null): boolean {
  if (!links) return false;
  return Boolean(links.issueUrl || links.prUrl || links.qaUrl || links.branch || links.repo);
}

/** Color del chip de la etapa actual (el de su peldaño, o el de fábrica). */
export function currentStageColor(devStage: DevStage | null | undefined): string {
  if (!devStage?.current) return "#64748b";
  return (
    devStage.timeline?.find((s) => s.id === devStage.current)?.color ||
    DEV_STAGE_META[devStage.current]?.color ||
    "#64748b"
  );
}

/**
 * Sólo se aceptan enlaces http(s) al pintarlos como <a href>: el backend ya los
 * sanea al guardar, pero un documento anterior a ese saneo (o editado a mano)
 * no debe poder colar un `javascript:` en la ficha.
 */
export function safeHref(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;
  const value = url.trim();
  return /^https?:\/\//i.test(value) ? value : null;
}
